import { Dispatch, JSX, SetStateAction } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ProfileForm } from "@/pages/gate/ProfilesPage";

import Button from "@/components/buttons/Button";
import Carousel from "@/components/helpers/Carousel.tsx";
import { IconBack } from "@/components/icons/main.icons";

import { ProfilePicture } from "@/api/user/user.types.ts";
import { useGetProfilePicturesCategory } from "@/hooks/api/user.hooks.ts";

import { ProfileTabs } from "./types";

const ChooseProfileImage = ({ setTab }: { setTab: Dispatch<SetStateAction<ProfileTabs>> }): JSX.Element => {
  const { data, isError } = useGetProfilePicturesCategory();
  const { setValue } = useFormContext<ProfileForm>();

  if (isError) {
    setTab(ProfileTabs.PROFILES);
  }

  const hexToRgba = (hex: string, opacity: number): string => {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const handleClick = (picture: ProfilePicture["picture"]): void => {
    setValue("picture", picture);
    setTab(ProfileTabs.ADD_PROFILE);
  };

  const onBack = (): void => {
    setTab(ProfileTabs.ADD_PROFILE);
  };

  const { t } = useTranslation();

  return (
    <>
      <div className={"mt-10 w-full space-y-6"}>
        <div className="flex w-full items-center justify-between">
          <div>
            <Button
              color={"black"}
              aria-label={t("Actions.back")}
              size={"medium"}
              icon={<IconBack className={"stroke-on-primary size-5"} />}
              onClick={onBack}
            >
              <span>{t("Actions.back")}</span>
            </Button>
          </div>
          <div className="text-center text-[36px] font-semibold">{t("Profile.choose-profile-image")}</div>
          <div></div>
        </div>
        {data.map((category) => (
          <div key={category.id} className={""}>
            <h2 className={"mb-4 text-2xl font-semibold"}>{category.title}</h2>
            <Carousel
              items={category.pictures}
              swiperParams={{
                spaceBetween: 16,
                slidesPerView: 3.2,
                slidesPerGroup: 3,
                breakpoints: {
                  400: { slidesPerView: 4.2, slidesPerGroup: 4 },
                  500: { slidesPerView: 5.2, slidesPerGroup: 5 },
                  768: { slidesPerView: 6.2, slidesPerGroup: 6 },
                  992: { slidesPerView: 7.2, slidesPerGroup: 7 },
                  1200: { slidesPerView: 8.2, slidesPerGroup: 8, spaceBetween: 20 },
                  1400: { slidesPerView: 9.2, slidesPerGroup: 9, spaceBetween: 20 },
                  1600: { slidesPerView: 10.2, slidesPerGroup: 10, spaceBetween: 20 },
                },
              }}
              swiperName={category.id.toString()}
              className={"-mx-4 mt-5 px-4"}
            >
              {(item) => {
                const color = item.picture?.color
                  ? `linear-gradient(${hexToRgba(item.picture.color, 0.6)} 0%, ${hexToRgba(item.picture.color, 0.25)} 100%)`
                  : "linear-gradient(rgba(255, 75, 0, 0.6) 0%, rgba(255, 75, 0, 0.25) 100%)";

                return (
                  <button
                    type={"button"}
                    aria-label={category.title + " " + item.id}
                    onClick={() => handleClick(item.picture)}
                    className={
                      "aspect-square w-full overflow-hidden rounded-full outline outline-2 -outline-offset-2 outline-transparent transition-outline hover:outline-orange-1"
                    }
                    style={{
                      background: color,
                    }}
                  >
                    <img
                      src={item.picture?.resized}
                      alt={"profile-image"}
                      className={"h-full w-full object-contain max-sm:size-[6.25rem]"}
                    />
                  </button>
                );
              }}
            </Carousel>
          </div>
        ))}
      </div>
    </>
  );
};

export default ChooseProfileImage;
